import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE,
    PENDING,
    STORAGE_AWS,
    STORAGE_GOOGLE,
    STORAGE_MICROSOFT,
} from "const"
import { MtDriveFolder, MtDriveFile } from "server/models"
import {
    StoreType,
    DriveFilters,
    DriveFolder,
    DriveFile,
    NestedFolder,

    LoadNestedFolders,

    setDriveFilters,
    setDriveSearchState,
    setDriveFolderList,
    setDriveFileList,

    reloadDriveLists,
    createDriveUrl,
    createFileName,
    createRecycleFileName,
    createFolderName,
} from "client/store"
import { search } from "client/store/utils"
import { deepCopy } from "client/utils"
import {
    SearchBar,
    SearchFilter,
} from "client/components"

let timerId: ReturnType<typeof setTimeout>

const Search = () => {
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const baseRoute = useSelector((state: StoreType) => state.drive.baseRoute)

    const filters = useSelector((state: StoreType) => state.drive.filters)
    const searchState = useSelector((state: StoreType) => state.drive.searchState)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const searchNested = (popupFilters: DriveFilters) => {
        // reload lists if filters are empty
        if (
            !popupFilters.owner
            && !popupFilters.storage
            && (!popupFilters.search || popupFilters.search === "")
        ) {
            reloadDriveLists(dispatch)
            return
        }

        dispatch(setDriveSearchState(PENDING))
        const targetFolder = drivePath && drivePath.length ? drivePath[drivePath.length - 1] : null

        const folders: DriveFolder[] = []
        const files: DriveFile[] = []

        const searchFiles = (targetFiles: MtDriveFile[], drivePathArray: MtDriveFolder[]) => {
            targetFiles.forEach((file) => {
                let showRow = true
                if (popupFilters.owner && file.owner !== popupFilters.owner) showRow = false
                else if (popupFilters.storage && file.storage !== popupFilters.storage) showRow = false
                else if (popupFilters.search) showRow = search(popupFilters.search, file)

                if (showRow) {
                    const folderUrl = createDriveUrl(drivePathArray, baseRoute)
                    const name = createFileName(file)
                    const recycleName = createRecycleFileName(file)
                    files.push({
                        ...file,
                        url: file.folderId === null
                            ? `${folderUrl}/${encodeURIComponent(recycleName)}`
                            : `${folderUrl}/${encodeURIComponent(name)}`,
                    })
                }
            })
        }

        const searchFolders = (targetFolders: NestedFolder[], drivePathArray: MtDriveFolder[]) => {
            targetFolders.forEach((nestedFolder) => {
                let showRow = true
                if (popupFilters.owner) showRow = false
                else if (popupFilters.storage) showRow = false
                else if (popupFilters.search) {
                    const searchFolder: MtDriveFolder = {
                        ...nestedFolder.folder,
                        name: createFolderName(nestedFolder.folder), // to allow search project and QID folders
                    }
                    showRow = search(popupFilters.search, searchFolder)
                }

                const newDrivePath: MtDriveFolder[] = deepCopy(drivePathArray)
                newDrivePath.push(nestedFolder.folder)

                if (showRow) {
                    folders.push({
                        ...nestedFolder.folder,
                        url: createDriveUrl(newDrivePath, baseRoute),
                        path: newDrivePath,
                    })
                }

                searchFolders(Object.values(nestedFolder.folders), newDrivePath)
                searchFiles(Object.values(nestedFolder.files), newDrivePath)
            })
        }

        LoadNestedFolders(userData.accountId, targetFolder, lang)
        .then((nestedFolders) => {
            const searchDrivePath = drivePath ? deepCopy(drivePath) : []
            searchFiles(Object.values(nestedFolders.files), searchDrivePath)
            searchFolders(Object.values(nestedFolders.folders), searchDrivePath)

            dispatch(setDriveFolderList(folders))
            dispatch(setDriveFileList(files))
            dispatch(setDriveSearchState(IDLE))
        })
    }

    const onFilter = (
        resFilters: DriveFilters,
        timeout: boolean,
    ) => {
        if (searchState === PENDING) return
        dispatch(setDriveFilters(resFilters))

        if (timeout) {
            clearTimeout(timerId)
            timerId = setTimeout(() => searchNested(resFilters), 1000)
        } else {
            searchNested(resFilters)
        }
    }

    const popupFilters: SearchFilter[] = [
        {
            title: lang.myFiles,
            isActive: filters.owner === userData.userId,
            onAdd: (obj: DriveFilters) => ({
                ...obj,
                owner: userData.userId,
            }),
            onClose: (obj: DriveFilters) => ({
                ...obj,
                owner: null,
            }),
        },
        {
            title: lang.awsStorage,
            isActive: filters.storage === STORAGE_AWS,
            onAdd: (obj: DriveFilters) => ({
                ...obj,
                storage: STORAGE_AWS,
            }),
            onClose: (obj: DriveFilters) => ({
                ...obj,
                storage: null,
            }),
        },
        {
            title: lang.googleStorage,
            isActive: filters.storage === STORAGE_GOOGLE,
            onAdd: (obj: DriveFilters) => ({
                ...obj,
                storage: STORAGE_GOOGLE,
            }),
            onClose: (obj: DriveFilters) => ({
                ...obj,
                storage: null,
            }),
        },
        {
            title: lang.microsoftOneDriveStorage,
            isActive: filters.storage === STORAGE_MICROSOFT,
            onAdd: (obj: DriveFilters) => ({
                ...obj,
                storage: STORAGE_MICROSOFT,
            }),
            onClose: (obj: DriveFilters) => ({
                ...obj,
                storage: null,
            }),
        },
    ]

    return (
        <SearchBar
            id="driveSearch"
            filters={filters}
            popupFilters={popupFilters}
            onFilter={(newFilters) => {
                if (
                    newFilters.search !== filters.search
                    && newFilters.search !== null
                ) {
                    onFilter(newFilters, true)
                } else {
                    onFilter(newFilters, false)
                }
            }}
        />
    )
}
export default Search
